import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import GoogleAuthButton from "@/components/GoogleAuthButton";
import { extractErrorMessage } from "@/lib/apiError";

const C = {
  bgDark: "#000",
  panel: "#0A2826",
  teal: "#0A8B85",
  tealHover: "#066667",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

const ClinqLogo = ({ withTagline = true }) => (
  <div className="flex items-center gap-3" data-testid="brand-logo-signup">
    <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
      style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
      Cl
      <span className="relative inline-block">
        ı
        <span aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
          style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span>
      </span>
      nq
    </span>
    {withTagline && (
      <div className="hidden sm:flex flex-col leading-none border-l pl-3 border-white/15">
        <span className="text-[9px] uppercase tracking-[0.22em] font-semibold" style={{ color: C.tealSoft }}>Funder</span>
        <span className="text-[9px] uppercase tracking-[0.22em] font-semibold mt-0.5 text-white/55">Readiness</span>
      </div>
    )}
  </div>
);

export default function Signup() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const [resendBusy, setResendBusy] = useState(false);

  const resend = async () => {
    setResendBusy(true);
    try {
      await api.post("/auth/resend-verification", { email: sentTo });
      toast.success("Fresh verification email sent — check your inbox.");
    } catch {
      toast.error("Could not resend. Please try again.");
    } finally {
      setResendBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { name, email, password };
      // Forward UTM tracking from query string (e.g. clinq.africa attribution)
      ["utm_source", "utm_campaign", "utm_medium"].forEach((k) => {
        const v = params.get(k);
        if (v) body[k] = v;
      });
      const { data } = await api.post("/auth/signup", body);
      if (data.verification_required) {
        setSentTo(data.email || email);
        return;
      }
      if (data.token) localStorage.setItem("session_token", data.token);
      setUser(data.user);

      // Forward prefill_* params to the wizard so they survive signup
      const forward = new URLSearchParams();
      params.forEach((v, k) => {
        if (k.startsWith("prefill_") || k.startsWith("utm_") || k === "from") forward.set(k, v);
      });
      const qs = forward.toString();
      navigate(qs ? `/dashboard?${qs}` : "/dashboard");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Signup failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col text-white"
      style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }} data-testid="signup-page">
      <SEO
        title="Sign up"
        description="Create a free Clinq Prep account and start building a funder-ready pitch deck, financial model and management accounts — all tailored to your business stage and industry."
        path="/signup"
      />
      <header style={{ backgroundColor: "#000" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/"><ClinqLogo /></Link>
          <Link to="/login" data-testid="header-login"
            className="text-sm font-medium text-white/90 hover:text-white px-3 py-2">Log In</Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        {sentTo ? (
          <div className="w-full max-w-md">
            <div className="rounded-2xl p-8 border text-center"
              style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}
              data-testid="signup-check-inbox">
              <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center"
                style={{ backgroundColor: "rgba(10,139,133,0.18)" }}>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={C.tealSoft} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                  <polyline points="22,6 12,13 2,6" />
                </svg>
              </div>
              <h1 className="font-extrabold text-2xl mt-5 tracking-tight">Verify your email</h1>
              <p className="mt-3 text-sm text-white/65 leading-relaxed">
                We've sent a verification email to{" "}
                <span className="font-semibold" style={{ color: C.tealSoft }} data-testid="signup-sent-email">{sentTo}</span>.
                Click the <strong>Verify My Email Address</strong> button in that email to activate your account.
                The link expires in 24 hours.
              </p>
              <button onClick={resend} disabled={resendBusy} data-testid="signup-resend-btn"
                className="mt-6 w-full py-2.5 rounded-md font-semibold text-sm disabled:opacity-60"
                style={{ backgroundColor: C.teal, color: "#fff" }}>
                {resendBusy ? "Sending…" : "Resend Verification Email"}
              </button>
              <p className="mt-4 text-xs text-white/40">
                Can't find it? Check your spam folder — the email comes from noreply@clinq.africa.
              </p>
            </div>
            <div className="mt-6 text-center text-sm text-white/60">
              Already verified?{" "}
              <Link to="/login" data-testid="signup-verified-to-login" className="font-semibold hover:underline" style={{ color: C.tealSoft }}>
                Log in
              </Link>
            </div>
          </div>
        ) : (
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-white/50">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span>
              Get funder-ready
            </div>
            <h1 className="font-extrabold text-4xl mt-3 tracking-tight" style={{ letterSpacing: "-0.03em" }}>
              Create your <span style={{ color: C.teal }}>Clinq</span> account
            </h1>
            <p className="mt-2 text-sm text-white/60">Build a bespoke financial model in minutes.</p>
          </div>

          <div className="rounded-2xl p-7 border" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
            <GoogleAuthButton label="Sign up with Google" testid="signup-google-btn" />

            <div className="flex items-center gap-3 my-5" aria-hidden="true">
              <div className="h-px flex-1" style={{ backgroundColor: "rgba(255,255,255,0.08)" }} />
              <span className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-semibold">or use email</span>
              <div className="h-px flex-1" style={{ backgroundColor: "rgba(255,255,255,0.08)" }} />
            </div>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Name</label>
                <input
                  type="text" value={name} onChange={(e) => setName(e.target.value)} required
                  data-testid="signup-name-input"
                  placeholder="Jane Founder"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md focus:outline-none focus:border-[#0A8B85] text-sm text-white placeholder:text-white/30"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Email</label>
                <input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                  data-testid="signup-email-input"
                  placeholder="founder@yourco.com"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md focus:outline-none focus:border-[#0A8B85] text-sm text-white placeholder:text-white/30"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Password</label>
                <input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6}
                  data-testid="signup-password-input"
                  placeholder="At least 6 characters"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md focus:outline-none focus:border-[#0A8B85] text-sm text-white placeholder:text-white/30"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
                />
              </div>
              <button
                type="submit" disabled={busy} data-testid="signup-submit-btn"
                className="w-full py-3 rounded-md font-semibold text-sm disabled:opacity-60 transition-colors"
                style={{ backgroundColor: C.teal, color: "#fff" }}
                onMouseOver={(e) => !busy && (e.currentTarget.style.backgroundColor = C.tealHover)}
                onMouseOut={(e) => (e.currentTarget.style.backgroundColor = C.teal)}>
                {busy ? "Creating…" : "Create account"}
              </button>
              <div className="text-center text-[12px] text-white/45 mt-3" data-testid="signup-bonus-note">
                Includes <span className="font-semibold" style={{ color: C.tealSoft }}>5 free credits</span> to try AI drafting — no card needed.
              </div>
            </form>
          </div>

          <div className="mt-6 text-center text-sm text-white/60">
            Already have an account?{" "}
            <Link to="/login" data-testid="signup-to-login" className="font-semibold hover:underline" style={{ color: C.tealSoft }}>
              Log in
            </Link>
          </div>
        </div>
        )}
      </main>
    </div>
  );
}
